import type { FC, ReactNode } from 'react'
import type { ServiceArea } from '@/types'

import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TransitionLink } from '@/components/generals/transition-link'
import { serviceAreas } from '@/constants/areas'

const SLOWEST = Math.max(...serviceAreas.map(area => area.arrivalMinutes))

interface AreaCardProps {
    area: ServiceArea
    className?: string
}

export const AreaCard: FC<AreaCardProps> = ({ area, className }): ReactNode => {
    const isYard = area.slug === 'riverton'

    return (
        <TransitionLink
            href={`/service-areas/${area.slug}`}
            className={cn(
                'group flex h-full flex-col justify-between gap-10 rounded-[26px] border border-ink/[0.08] bg-card p-6 transition-[border-color,background-color,transform,translate,scale,rotate] duration-500 ease-expo hover:-translate-y-1 hover:border-ink/20 hover:bg-paper sm:p-7',
                className
            )}
        >
            <div className='flex items-start justify-between gap-4'>
                <div>
                    <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-slate'>
                        {area.zips.join(' · ')}
                    </p>

                    <h3 className='mt-3 font-heading text-[1.65rem] font-semibold leading-none tracking-[-0.035em] text-ink transition-colors duration-300 group-hover:text-ember sm:text-[1.85rem]'>
                        {area.name}
                    </h3>

                    {(isYard || area.doorsServiced) && (
                        <p className='mt-3 inline-flex rounded-full bg-sand px-3 py-1 text-[12.5px] text-ink'>
                            {isYard ? "Our trucks start here" : `${area.doorsServiced} doors serviced here`}
                        </p>
                    )}
                </div>

                <span className='flex size-11 shrink-0 items-center justify-center rounded-full border border-ink/15 text-ink transition-[background-color,border-color,color,transform,translate,scale,rotate] duration-500 ease-expo group-hover:rotate-45 group-hover:border-ember group-hover:bg-ember group-hover:text-white'>
                    <ArrowUpRight className='size-4' aria-hidden='true' />
                </span>
            </div>

            <div>
                <div className='flex items-baseline justify-between gap-4'>
                    <span className='text-[13.5px] text-slate'>Usual arrival, urgent calls</span>
                    <span className='font-mono text-[15px] text-ink'>{area.arrival}</span>
                </div>

                <div className='mt-3 h-1.5 overflow-hidden rounded-full bg-sand'>
                    <div
                        className='h-full origin-left rounded-full bg-ember transition-transform duration-700 ease-expo group-hover:scale-x-[1.02]'
                        style={{ width: `${(area.arrivalMinutes / SLOWEST) * 100}%` }}
                    />
                </div>
            </div>
        </TransitionLink>
    )
}
